import { useAuthenticator } from '@aws-amplify/ui-react';
import { Button, Spinner } from '@nrg/design-system';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { cognitoErrorAuthKey } from '@/auth/cognito-error-message';
import AuthFooterLink from '@/auth/components/AuthFooterLink';
import AuthScreen from '@/auth/components/AuthScreen';
import FormField from '@/auth/components/FormField';
import PasswordField from '@/auth/components/PasswordField';

const schema = z.object({
  username: z.string().trim().email(),
  password: z.string().min(1),
});

type FieldErrors = Partial<Record<'username' | 'password', string>>;

export default function SignInScreen() {
  const { t } = useTranslation('auth');
  const { isPending, error, submitForm, toForgotPassword } = useAuthenticator((c) => [
    c.isPending,
    c.error,
    c.route,
  ]);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const parsed = schema.safeParse({ username, password });
  const fieldErrors: FieldErrors = {};
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const path = issue.path[0];
      if (path === 'username' && (touched.username || submitAttempted)) {
        fieldErrors.username = t('errors.email_invalid');
      }
      if (path === 'password' && (touched.password || submitAttempted)) {
        fieldErrors.password = t('errors.password_required');
      }
    }
  }

  const cognitoKey = cognitoErrorAuthKey(error);
  const banner = cognitoKey ? t(cognitoKey) : null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (!parsed.success) return;
    submitForm({ username: parsed.data.username, password: parsed.data.password });
  };

  return (
    <AuthScreen
      heading={t('sign_in.heading')}
      subhead={t('sign_in.subhead')}
      error={banner}
      onSubmit={handleSubmit}
      footer={
        <AuthFooterLink onClick={() => toForgotPassword()}>{t('sign_in.forgot')}</AuthFooterLink>
      }
    >
      <FormField
        name="username"
        label={t('sign_in.email')}
        type="email"
        autoComplete="username"
        autoFocus
        spellCheck={false}
        autoCapitalize="off"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, username: true }))}
        error={fieldErrors.username}
        disabled={isPending}
      />
      <PasswordField
        name="password"
        label={t('sign_in.password')}
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
        error={fieldErrors.password}
        disabled={isPending}
      />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={isPending}
        iconLeft={isPending ? <Spinner size="sm" /> : undefined}
      >
        {t('sign_in.submit')}
      </Button>
    </AuthScreen>
  );
}
